<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/custom-event-name-casing */
import { ref } from 'vue';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  allowEdit: {
    type: Boolean,
    default: false,
  },
  maxDiscountPercent: {
    type: Number,
    default: null,
  },
  generalDiscount: {
    type: Number,
    default: 0,
  },
  discountPercent: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['update-price']);

const isOpen = ref(false);
const close = () => {
  isOpen.value = false;
};
const toggle = () => {
  isOpen.value = !isOpen.value;
};

const currentPrice = () => props.item.customPrice ?? props.item.price;
const hasTiers = () =>
  props.allowEdit && (props.item.availablePrices?.length ?? 0) > 1;

const formatPrice = price =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    price
  );

const setPrice = rawPrice => {
  const tablePrice = props.item.price;
  const upperBoundPercent =
    props.maxDiscountPercent != null
      ? Math.min(
          99.99,
          Math.max(0, props.maxDiscountPercent - props.generalDiscount)
        )
      : 99.99;
  const minAllowedPrice =
    Math.round(tablePrice * (1 - upperBoundPercent / 100) * 100) / 100;
  const clamped = Math.min(tablePrice, Math.max(minAllowedPrice, rawPrice));

  emit('update-price', props.item.id, clamped);
};

const handleInput = value => {
  const parsed = parseFloat(String(value).replace(',', '.'));
  if (Number.isNaN(parsed) || parsed < 0) return;
  setPrice(parsed);
};

// Tier prices come straight from IntegraHub's own price table (Tabela/Promoção),
// so they aren't subject to maxDiscountPercent — that cap only applies when the
// agent types in a custom price/discount by hand (see handleInput above).
const selectTier = price => {
  emit('update-price', props.item.id, price);
  close();
};
</script>

<template>
  <span v-if="!allowEdit" class="text-sm font-medium text-n-text">
    {{ formatPrice(item.price) }}
  </span>
  <div v-else v-on-clickaway="close" class="relative inline-block">
    <div
      class="inline-flex items-center rounded border py-0.5 px-2 gap-1"
      :class="discountPercent > 0 ? 'border-green-400' : 'border-n-weak'"
    >
      <input
        type="text"
        inputmode="decimal"
        :value="currentPrice().toFixed(2)"
        size="6"
        class="reset-base shrink-0 appearance-none text-sm leading-tight font-semibold text-right bg-transparent !border-none !shadow-none !outline-none p-0 m-0"
        :class="discountPercent > 0 ? 'text-green-600' : 'text-n-text'"
        @change="e => handleInput(e.target.value)"
      />
      <button
        v-if="hasTiers()"
        type="button"
        class="shrink-0 leading-none"
        @click="toggle"
      >
        <span
          class="i-lucide-chevron-down block w-3.5 h-3.5 text-n-text-subtle transition-transform"
          :class="{ 'rotate-180': isOpen }"
        />
      </button>
    </div>
    <div
      v-if="hasTiers() && isOpen"
      class="mt-1 rounded border border-n-weak bg-n-solid-2 absolute z-10 w-max min-w-full"
    >
      <button
        v-for="opt in item.availablePrices"
        :key="opt.price"
        type="button"
        class="w-full flex items-center justify-between px-2 py-1 text-sm text-left hover:bg-n-slate-3"
        @click="selectTier(opt.price)"
      >
        <span>{{ opt.label }} – {{ formatPrice(opt.price) }}</span>
        <span
          v-if="opt.price === currentPrice()"
          class="i-lucide-check ml-1 shrink-0 text-n-brand"
        />
      </button>
    </div>
  </div>
</template>
